import React from 'react';
import type { ThemePalette } from '../../types';
import { useAIStore } from '../../stores/aiStore';
import { usedViaPointer } from '../../onboarding';

interface TopToolbarProps {
  // Application State
  pagesLength: number;
  isViewOnly: boolean;
  hasDiagramContent: boolean;
  showLocalDevTools: boolean;
  isJsonPanelOpen: boolean;
  isDistractionFree: boolean;
  
  // Selection State
  canGroupSelected: boolean;
  selectedNodeIdsLength: number;

  // Local Toolbar State Handlers
  isToolbarExpanded: boolean;
  setIsToolbarExpanded: (v: boolean | ((prev: boolean) => boolean)) => void;
  toolbarCollapseTimer: ReturnType<typeof setTimeout> | null;
  setToolbarCollapseTimer: (t: ReturnType<typeof setTimeout> | null) => void;
  openDropdown: 'new' | 'file' | 'tools' | null;
  setOpenDropdown: (v: 'new' | 'file' | 'tools' | null | ((prev: 'new' | 'file' | 'tools' | null) => 'new' | 'file' | 'tools' | null)) => void;

  // Global Actions
  handleCreateProject: (type: 'flow' | 'sequence' | 'c4') => void;
  handleSave: () => void;
  handleSaveAs: () => void;
  handleLoad: () => void;
  handlePublishLink: () => void;
  setExportAllPagesAsPng: (v: boolean) => void;
  setIsPngExportModalOpen: (v: boolean) => void;
  handleExportImage: (format: 'svg' | 'png') => void;
  setIsProjectManagerOpen: (v: boolean) => void;
  setIsPageManagerOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  setIsMermaidModalOpen: (v: boolean) => void;
  setIsJsonPanelOpenHandler: (v: boolean | ((prev: boolean) => boolean)) => void;
  setIsDevStateViewerOpen: (v: boolean) => void;
  setIsSettingsOpen: (v: boolean) => void;
  setIsFeedbackOpen: (v: boolean) => void;
  setIsChordModalOpen: (v: boolean) => void;
  setIsDistractionFreeHandler: (v: boolean | ((prev: boolean) => boolean)) => void;
  handleGroupSelected: () => void;
  onStartTutorial?: () => void;

  // Refs & Styles
  fileInputRef: React.RefObject<HTMLInputElement>;
  glassStyle: React.CSSProperties;
  btnGhostClass: string;
  palette: ThemePalette;
}

export function TopToolbar({
  pagesLength, isViewOnly, hasDiagramContent, showLocalDevTools, isJsonPanelOpen, isDistractionFree,
  canGroupSelected, selectedNodeIdsLength,
  isToolbarExpanded, setIsToolbarExpanded, toolbarCollapseTimer, setToolbarCollapseTimer, openDropdown, setOpenDropdown,
  handleCreateProject, handleSave, handleSaveAs, handleLoad, handlePublishLink, setExportAllPagesAsPng, setIsPngExportModalOpen, handleExportImage,
  setIsProjectManagerOpen, setIsPageManagerOpen, setIsMermaidModalOpen, setIsJsonPanelOpenHandler, setIsDevStateViewerOpen,
  setIsSettingsOpen, setIsFeedbackOpen, setIsChordModalOpen, setIsDistractionFreeHandler, handleGroupSelected, onStartTutorial,
  fileInputRef, glassStyle, btnGhostClass, palette
}: TopToolbarProps) {
  return (
    <div
      className="absolute top-4 right-4 z-50 flex items-center gap-2"
      data-tour="toolbar"
      onMouseEnter={() => {
        if (toolbarCollapseTimer) { clearTimeout(toolbarCollapseTimer); setToolbarCollapseTimer(null); }
        setIsToolbarExpanded(true);
      }}
      onMouseLeave={() => {
        const t = setTimeout(() => { setIsToolbarExpanded(false); setOpenDropdown(null); }, 4000);
        setToolbarCollapseTimer(t);
      }}
    >
      {/* Collapsed indicator pill */}
      {!isToolbarExpanded && (
        <div
          style={glassStyle}
          className="backdrop-blur-md border rounded-xl px-3 py-1.5 shadow-2xl flex items-center gap-1.5 cursor-default select-none"
        >
          <span className="flex items-center gap-1.5 text-xs font-medium" style={{ color: palette.panelSubText }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
            Menu {isViewOnly ? '(View)' : ''}
          </span>
        </div>
      )}

      {/* Expanded toolbar */}
      <div
        className="flex items-center gap-1 transition-all duration-300"
        style={{
          maxWidth: isToolbarExpanded ? '800px' : '0px',
          opacity: isToolbarExpanded ? 1 : 0,
          pointerEvents: isToolbarExpanded ? 'auto' : 'none',
        }}
      >
        <div style={glassStyle} className="backdrop-blur-md border rounded-xl px-2 py-1.5 shadow-2xl flex items-center gap-0.5">
          {!isViewOnly && (
            <>
              {/* ── New dropdown ── */}
              <div className="relative">
                <button
                  className={btnGhostClass}
                  style={{ color: palette.panelSubText }}
                  onClick={() => setOpenDropdown(v => v === 'new' ? null : 'new')}
                  title="New diagram"
                  data-tour="toolbar-new"
                >New</button>
                {openDropdown === 'new' && (
                  <div
                    className="absolute right-0 top-full mt-1 rounded-xl border shadow-2xl backdrop-blur-md overflow-hidden z-[200] w-40"
                    style={{ background: palette.panelBg, borderColor: palette.panelBorder }}
                  >
                    <button className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors" style={{ color: palette.panelText }}
                      onClick={() => handleCreateProject('flow')}>New Flow</button>
                    <button className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors" style={{ color: palette.panelText }}
                      onClick={() => handleCreateProject('sequence')}>New Sequence</button>
                    <button className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors" style={{ color: palette.panelText }}
                      onClick={() => handleCreateProject('c4')}>New C4 workspace</button>
                  </div>
                )}
              </div>

              <div className="w-px h-4 mx-0.5" style={{ background: palette.panelBorder }} />
            </>
          )}

          {/* ── File dropdown (floppy icon) ── */}
          <div className="relative">
            <button
              className={btnGhostClass}
              style={{ color: palette.panelSubText }}
              onClick={() => setOpenDropdown(v => v === 'file' ? null : 'file')}
              title="Save / Load"
              data-tour="toolbar-file"
            >File</button>
            {openDropdown === 'file' && (
              <div
                className="absolute right-0 top-full mt-1 rounded-xl border shadow-2xl backdrop-blur-md overflow-hidden z-[200] w-40"
                style={{ background: palette.panelBg, borderColor: palette.panelBorder }}
              >
                {!isViewOnly && (
                  <button
                    className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors"
                    style={{ color: hasDiagramContent ? palette.panelText : palette.panelBorder, cursor: hasDiagramContent ? 'pointer' : 'not-allowed' }}
                    disabled={!hasDiagramContent}
                    onClick={() => { if (hasDiagramContent) { handleSave(); setOpenDropdown(null); } }}
                  >Save</button>
                )}
                {!isViewOnly && (
                  <>
                    <button
                      className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors"
                      style={{ color: hasDiagramContent ? palette.panelText : palette.panelBorder, cursor: hasDiagramContent ? 'pointer' : 'not-allowed' }}
                      disabled={!hasDiagramContent}
                      onClick={() => { if (hasDiagramContent) { handleSaveAs(); setOpenDropdown(null); } }}
                    >Save As...</button>
                    <div className="h-px mx-2 my-0.5" style={{ background: palette.panelBorder }} />
                    <button
                      className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors"
                      style={{ color: palette.panelText }}
                      onClick={() => { window.showOpenFilePicker ? handleLoad() : fileInputRef.current?.click(); setOpenDropdown(null); }}
                    >Load File...</button>
                  </>
                )}
                {!isViewOnly && <div className="h-px mx-2 my-0.5" style={{ background: palette.panelBorder }} />}
                <button
                  className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors"
                  style={{ color: hasDiagramContent ? palette.panelText : palette.panelBorder, cursor: hasDiagramContent ? 'pointer' : 'not-allowed' }}
                  disabled={!hasDiagramContent}
                  onClick={() => { if (hasDiagramContent) { handlePublishLink(); setOpenDropdown(null); } }}
                >Publish Link</button>
                <button
                  className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors"
                  style={{ color: palette.panelText }}
                  onClick={() => {
                    setExportAllPagesAsPng(false);
                    setIsPngExportModalOpen(true);
                    setOpenDropdown(null);
                  }}
                >Export PNG</button>
                <button
                  className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors"
                  style={{ color: palette.panelText }}
                  onClick={() => { handleExportImage('svg'); setOpenDropdown(null); }}
                >Export SVG</button>
              </div>
            )}
          </div>

          <div className="w-px h-4 mx-0.5" style={{ background: palette.panelBorder }} />

          {/* ── Projects ── */}
          {!isViewOnly && (
            <>
              <button
                onClick={() => { setIsProjectManagerOpen(true); setOpenDropdown(null); }}
                className={btnGhostClass}
                style={{ color: palette.panelSubText }}
                title="Projects"
              >Projects</button>

              <div className="w-px h-4 mx-0.5" style={{ background: palette.panelBorder }} />
            </>
          )}

          {/* ── Tools dropdown ── */}
          <div className="relative">
            <button
              className={btnGhostClass}
              style={{ color: palette.panelSubText }}
              onClick={() => setOpenDropdown(v => v === 'tools' ? null : 'tools')}
              title="Tools"
              data-tour="toolbar-tools"
            >Tools</button>
            {openDropdown === 'tools' && (
              <div
                className="absolute right-0 top-full mt-1 rounded-xl border shadow-2xl backdrop-blur-md overflow-hidden z-[200] w-44"
                style={{ background: palette.panelBg, borderColor: palette.panelBorder }}
              >
                <button className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors" style={{ color: palette.panelText }}
                  onClick={() => { setIsPageManagerOpen(v => !v); setOpenDropdown(null); }}>Pages ({pagesLength})</button>
                {!isViewOnly && (
                  <button className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors" style={{ color: palette.panelText }}
                    onClick={() => { setIsMermaidModalOpen(true); setOpenDropdown(null); }}>Mermaid</button>
                )}
                {!isViewOnly && (
                  <>
                    {showLocalDevTools && (
                      <>
                        <div className="h-px mx-2 my-0.5" style={{ background: palette.panelBorder }} />
                        <button className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors" style={{ color: isJsonPanelOpen ? '#818cf8' : palette.panelText }}
                          onClick={() => { setIsJsonPanelOpenHandler(v => !v); setOpenDropdown(null); }}>{ '{ } JSON Editor'}</button>
                        <button className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors" style={{ color: palette.panelText }}
                          onClick={() => { setIsDevStateViewerOpen(true); setOpenDropdown(null); }}>Dev State</button>
                      </>
                    )}
                    <button className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors" style={{ color: palette.panelText }}
                      onClick={() => { setIsSettingsOpen(true); setOpenDropdown(null); }}>Settings</button>
                    <button className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors" style={{ color: palette.panelText }}
                      onClick={() => { setIsFeedbackOpen(true); setOpenDropdown(null); }}>Send Feedback</button>
                  </>
                )}
                <div className="h-px mx-2 my-0.5" style={{ background: palette.panelBorder }} />
                <button className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors" style={{ color: palette.panelText }}
                  onClick={() => { setIsChordModalOpen(true); setOpenDropdown(null); }}>Keyboard Shortcuts</button>
                {onStartTutorial && (
                  <button className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors" style={{ color: palette.panelText }}
                    onClick={() => { onStartTutorial(); setOpenDropdown(null); }}>Take the tour</button>
                )}
                <button className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors" style={{ color: isDistractionFree ? '#818cf8' : palette.panelText }}
                  onClick={() => { usedViaPointer('distraction-free'); setIsDistractionFreeHandler(v => !v); setOpenDropdown(null); }}>Distraction-Free (t+f)</button>
              </div>
            )}
          </div>
        </div>

        {/* Group button — appears when 2+ nodes selected */}
        {!isViewOnly && canGroupSelected && (
          <button
            onClick={handleGroupSelected}
            style={glassStyle}
            className="backdrop-blur-md border rounded-xl px-3 py-1.5 shadow-xl text-xs font-medium transition-colors"
            title={`Architecture group (${selectedNodeIdsLength} selected) · Ctrl+G`}
          >
            <span style={{ color: palette.panelText }}>Architecture group (Ctrl+G)</span>
          </button>
        )}
      </div>

      {/* Group button visible even when collapsed */}
      {!isToolbarExpanded && !isViewOnly && canGroupSelected && (
        <button
          onClick={handleGroupSelected}
          style={glassStyle}
          className="backdrop-blur-md border rounded-xl px-3 py-1.5 shadow-xl text-xs font-medium transition-colors"
          title={`Architecture group (${selectedNodeIdsLength} selected) · Ctrl+G`}
        >
          <span style={{ color: palette.panelText }}>Architecture group (Ctrl+G)</span>
        </button>
      )}

      {/* AI Chat toggle */}
      <AIToggleButton glassStyle={glassStyle} palette={palette} />

      <input ref={fileInputRef} type="file" accept=".velostruct,.velostruct,.json" className="hidden" onChange={handleLoad} />
    </div>
  );
}

function AIToggleButton({ glassStyle, palette }: { glassStyle: React.CSSProperties; palette: ThemePalette }) {
  const { isPanelOpen, togglePanel, backendStatus } = useAIStore();
  const dotColor = backendStatus === 'ready' ? '#22c55e' : backendStatus === 'unavailable' ? '#64748b' : '#f59e0b';
  return (
    <button
      onClick={() => { usedViaPointer('ai-chat'); togglePanel(); }}
      style={glassStyle}
      className="backdrop-blur-md border rounded-xl px-3 py-1.5 shadow-xl text-xs font-medium transition-colors flex items-center gap-1.5"
      title="Toggle AI Chat (Ctrl+Shift+A)"
      data-tour="ai-toggle"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: isPanelOpen ? '#a78bfa' : palette.panelSubText }}>
        <path d="M12 2a8 8 0 0 0-8 8c0 3.4 2.1 6.3 5 7.5V20h6v-2.5c2.9-1.2 5-4.1 5-7.5a8 8 0 0 0-8-8z" />
        <path d="M10 22h4" />
      </svg>
      <span style={{ color: isPanelOpen ? '#a78bfa' : palette.panelSubText }}>AI</span>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: dotColor }} />
    </button>
  );
}
